import {
  ConnectedPosition,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { DomPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  SecurityContext,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Where the tooltip goes: centred above the element ('top') or starting at
 * its left edge above it ('top-start'), each below the element when there is
 * no room above.
 */
export type TooltipPosition = 'top' | 'top-start';

const positions: Record<TooltipPosition, ConnectedPosition[]> = {
  top: [
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      panelClass: 'app-tooltip-top',
    },
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      panelClass: 'app-tooltip-bottom',
    },
  ],
  'top-start': [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      panelClass: 'app-tooltip-top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      panelClass: 'app-tooltip-bottom',
    },
  ],
};

/**
 * A tooltip on hover: `[appTooltip]` with a text, or with markup when
 * `tooltipEscape` is false (sanitized, or trusted SafeHtml). It opens on a CDK
 * overlay while the pointer is on the element and closes when it leaves or the
 * page scrolls.
 */
@Directive({
  selector: '[appTooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'show()',
    '(mouseleave)': 'hide()',
  },
})
export class TooltipDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private sanitizer = inject(DomSanitizer);
  private document = inject(DOCUMENT);

  @Input('appTooltip') content: string | SafeHtml | null | undefined = null;
  @Input() tooltipPosition: TooltipPosition = 'top';
  @Input() tooltipStyleClass = '';
  /** false shows the content as markup. */
  @Input() tooltipEscape = true;

  private overlayRef: OverlayRef | null = null;

  show() {
    if (this.overlayRef || !this.content) {
      return;
    }
    const tooltip = this.document.createElement('div');
    tooltip.className = `app-tooltip ${this.tooltipStyleClass}`.trim();
    const text = this.document.createElement('div');
    text.className = 'app-tooltip-text';
    if (this.tooltipEscape) {
      text.textContent = String(this.content);
    } else {
      text.innerHTML =
        this.sanitizer.sanitize(SecurityContext.HTML, this.content) ?? '';
    }
    tooltip.appendChild(text);
    // A DOM portal moves an element that has a parent.
    this.document.createElement('div').appendChild(tooltip);

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.element)
        .withPositions(positions[this.tooltipPosition])
        .withFlexibleDimensions(false)
        .withPush(false)
        .withViewportMargin(0),
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: 'app-tooltip-pane',
    });
    this.overlayRef.attach(new DomPortal(tooltip));
    this.overlayRef.detachments().subscribe(() => this.hide());
  }

  hide() {
    const ref = this.overlayRef;
    this.overlayRef = null;
    ref?.dispose();
  }

  ngOnDestroy(): void {
    this.hide();
  }
}
